import React, { ReactElement, useMemo, useState } from "react"
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table"
import { Box, IconButton, Menu, MenuItem, FormControlLabel, Checkbox } from "@mui/material"
import { Visibility } from "@mui/icons-material"
import { COLOR } from "utils/color"

interface ShipperAnalysisTableProps {
  data: any
}

export default function ShipperAnalysisTable({ data }: ShipperAnalysisTableProps): ReactElement {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [visibleMetrics, setVisibleMetrics] = useState<Record<string, boolean>>({})

  // Transform data for pivoted table: metrics as rows, periods as columns
  const pivotedData = useMemo(() => {
    // Define metrics with visibility settings
    const metrics = [
      // Default visible metrics (as requested)
      {
        key: "cat-volumes",
        label: "Volumes",
        formatter: () => "",
        visible: true,
        isCategory: true,
      },
      {
        key: "totalCompleted",
        label: "Total colis complétés",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: true,
      },
      {
        key: "totalDelivered",
        label: "Total livrés",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: true,
      },
      {
        key: "deliveredRate",
        label: "Taux de livraison",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: true,
      },
      {
        key: "cat-devoyes",
        label: "Dévoyés",
        formatter: () => "",
        visible: true,
        isCategory: true,
      },
      {
        key: "totalCancelled",
        label: "Total annulés",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: true,
      },
      {
        key: "totalCancelledWithoutFailure",
        label: "Total annulés sans échec de livraison",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: true,
      },
      {
        key: "totalReassigned",
        label: "Total réassignés",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: true,
      },
      {
        key: "cancellationRate",
        label: "Taux annulés",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: true,
      },
      {
        key: "cancellationWithoutFailureRate",
        label: "Taux annulés sans échec de livraison",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: true,
      },
      {
        key: "reassignmentRate",
        label: "Taux colis réassignés",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: true,
      },
      {
        key: "cat-comportement-client",
        label: "Comportement client",
        formatter: () => "",
        visible: true,
        isCategory: true,
      },
      {
        key: "averageReceptionToDeliveryTime",
        label: "Temps moyen entre réception et livraison",
        formatter: (value: number) => `${value.toFixed(2)}J`,
        visible: true,
      },
      {
        key: "totalReturned",
        label: "Total retournés",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: true,
      },
      {
        key: "returnedRate",
        label: "Taux de colis retournés",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: true,
      },
      {
        key: "cat-satisfaction-client",
        label: "Satisfaction client",
        formatter: () => "",
        visible: true,
        isCategory: true,
      },
      {
        key: "averageRate",
        label: "Note moyenne",
        formatter: (value: number | null) => (value !== null ? value.toFixed(2) : "N/A"),
        visible: true,
      },
      {
        key: "averageNps",
        label: "NPS moyen",
        formatter: (value: number | null) => (value !== null ? value.toFixed(2) : "N/A"),
        visible: true,
      },
      {
        key: "disputeRate",
        label: "Taux de litiges",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: true,
      },
      // Hidden by default metrics
      {
        key: "totalKeeperFailures",
        label: "Total échecs gardien",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: false,
      },
      {
        key: "totalCancelledWithFailure",
        label: "Total annulés avec échec",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: false,
      },
      {
        key: "totalDisputed",
        label: "Total en litige",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: false,
      },
      {
        key: "totalPudoRespected",
        label: "Total PUDO respectés",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: false,
      },
      {
        key: "totalFirstChoiceRespected",
        label: "Total premier choix respecté",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: false,
      },
      {
        key: "totalSurveyAnswered",
        label: "Total enquêtes répondues",
        formatter: (value: number) => value.toLocaleString("fr-FR"),
        visible: false,
      },
      {
        key: "keeperFailureRate",
        label: "Taux échecs gardien",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: false,
      },
      {
        key: "pudoRespectedRate",
        label: "Taux PUDO respecté",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: false,
      },
      {
        key: "firstChoiceRespectedRate",
        label: "Taux premier choix respecté",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: false,
      },
      {
        key: "surveyResponseRate",
        label: "Taux de réponse enquête",
        formatter: (value: number) => `${value.toFixed(2)}%`,
        visible: false,
      },
    ]

    return metrics.map(metric => {
      const row: any = {
        metric: metric.label,
        metricKey: metric.key,
        visible: metric.visible,
        isVisible: visibleMetrics[metric.key] !== undefined ? visibleMetrics[metric.key] : metric.visible,
        isCategory: metric.isCategory === true,
      }

      // Sort periods chronologically
      const sortedGrouped = [...data.grouped].sort(
        (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime(),
      )

      sortedGrouped.forEach((groupedItem, index) => {
        const periodKey = `period_${index}`
        let value: any

        value = groupedItem.data[metric.key]

        row[periodKey] = metric.formatter(value as any)
      })

      return row
    })
  }, [data, visibleMetrics])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleMetricToggle = (metricKey: string) => {
    setVisibleMetrics(prev => ({
      ...prev,
      [metricKey]: !prev[metricKey],
    }))
  }

  const filteredData = useMemo(() => {
    return pivotedData.filter(row => row.isVisible)
  }, [pivotedData])

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    const cols: MRT_ColumnDef<any>[] = [
      {
        accessorKey: "metric",
        header: "",
        size: 250,
        muiTableHeadCellProps: {
          sx: {
            fontWeight: "bold",
            backgroundColor: COLOR.BLUE,
            color: COLOR.WHITE,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            fontWeight: "600",
          },
        },
      },
    ]

    // Sort periods chronologically and add columns
    const sortedGrouped = [...data.grouped].sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime())

    sortedGrouped.forEach((groupedItem, index) => {
      const label = groupedItem.label

      cols.push({
        accessorKey: `period_${index}`,
        header: label,
        muiTableHeadCellProps: {
          sx: {
            textAlign: "center !important",
            fontWeight: "bold",
            backgroundColor: COLOR.BLUE,
            color: COLOR.WHITE,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            textAlign: "center",
          },
        },
      })
    })

    return cols
  }, [data])

  return (
    <Box sx={{ width: "100%" }}>
      <MaterialReactTable
        columns={columns}
        data={filteredData}
        enableRowSelection={false}
        enableColumnOrdering={false}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        enablePagination={false}
        enableSorting={false}
        enableBottomToolbar={false}
        enableTopToolbar={true}
        enableHiding={false}
        enableColumnActions={false}
        enableDensityToggle={true}
        enableFullScreenToggle={false}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", padding: 0, alignItems: "center", alignSelf: "center" }}>
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{
                color: COLOR.BLUE,
                "&:hover": {
                  backgroundColor: "rgba(52, 152, 219, 0.1)",
                },
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { maxHeight: 400, width: 300 },
              }}
            >
              {pivotedData.map(row => (
                <MenuItem key={row.metricKey} onClick={() => handleMetricToggle(row.metricKey)}>
                  <FormControlLabel
                    control={<Checkbox checked={row.isVisible} size="small" />}
                    label={row.metric}
                    sx={{ width: "100%" }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
        initialState={{
          density: "compact",
        }}
        muiTableProps={{
          sx: {
            tableLayout: "fixed",
            overflow: "hidden",
            boxShadow: "none",
            backgroundColor: "transparent !important",
          },
        }}
        muiTablePaperProps={{
          sx: {
            boxShadow: "none !important",
            overflow: "hidden",
            backgroundColor: "transparent !important",
          },
        }}
        muiTopToolbarProps={{
          sx: {
            backgroundColor: "transparent !important",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: "bold",
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            borderBottom: "1px solid #f4f4f4",
          },
        }}
        muiTableBodyRowProps={(row: any) => {
          const isCategory = row.row.original.isCategory
          if (isCategory) {
            return {
              sx: {
                backgroundColor: COLOR.SUPER_LIGHT_GREY,
                color: COLOR.WHITE,
              },
            }
          }
          return {}
        }}
        localization={{
          actions: "Actions",
          and: "et",
          cancel: "Annuler",
          changeFilterMode: "Changer le mode de filtre",
          changeSearchMode: "Changer le mode de recherche",
          clearFilter: "Effacer le filtre",
          clearSearch: "Effacer la recherche",
          clearSort: "Effacer le tri",
          clickToCopy: "Cliquer pour copier",
          collapse: "Réduire",
          collapseAll: "Tout réduire",
          columnActions: "Actions de colonne",
          copiedToClipboard: "Copié dans le presse-papiers",
          dropToGroupBy: "Glisser pour grouper par {column}",
          edit: "Modifier",
          expand: "Développer",
          expandAll: "Tout développer",
          filterArrIncludes: "Inclut",
          filterArrIncludesAll: "Inclut tous",
          filterArrIncludesSome: "Inclut certains",
          filterBetween: "Entre",
          filterBetweenInclusive: "Entre (inclusif)",
          filterByColumn: "Filtrer par {column}",
          filterContains: "Contient",
          filterEmpty: "Vide",
          filterEndsWith: "Se termine par",
          filterEquals: "Égal",
          filterEqualsString: "Égal",
          filterFuzzy: "Flou",
          filterGreaterThan: "Supérieur à",
          filterGreaterThanOrEqualTo: "Supérieur ou égal à",
          filterInNumberRange: "Entre",
          filterIncludesString: "Contient",
          filterIncludesStringSensitive: "Contient",
          filterLessThan: "Inférieur à",
          filterLessThanOrEqualTo: "Inférieur ou égal à",
          filterMode: "Mode de filtre: {filterType}",
          filterNotEmpty: "Non vide",
          filterNotEquals: "Différent de",
          filterStartsWith: "Commence par",
          filterWeakEquals: "Égal",
          filteringByColumn: "Filtrage par {column} - {filterType} - {filterValue}",
          goToFirstPage: "Aller à la première page",
          goToLastPage: "Aller à la dernière page",
          goToNextPage: "Aller à la page suivante",
          goToPreviousPage: "Aller à la page précédente",
          grab: "Saisir",
          groupByColumn: "Grouper par {column}",
          groupedBy: "Groupé par ",
          hideAll: "Tout masquer",
          hideColumn: "Masquer la colonne {column}",
          max: "Max",
          min: "Min",
          move: "Déplacer",
          noRecordsToDisplay: "Aucun enregistrement à afficher",
          noResultsFound: "Aucun résultat trouvé",
          of: "sur",
          or: "ou",
          pinToLeft: "Épingler à gauche",
          pinToRight: "Épingler à droite",
          resetColumnSize: "Réinitialiser la taille de la colonne",
          resetOrder: "Réinitialiser l'ordre",
          rowActions: "Actions de ligne",
          rowNumber: "Numéro de ligne",
          rowNumbers: "Numéros de ligne",
          rowsPerPage: "Lignes par page",
          save: "Enregistrer",
          search: "Rechercher",
          select: "Sélectionner",
          selectedCountOfRowCountRowsSelected: "{selectedCount} sur {rowCount} ligne(s) sélectionnée(s)",
          showAll: "Tout afficher",
          showAllColumns: "Afficher toutes les colonnes",
          showHideColumns: "Afficher/Masquer les colonnes",
          showHideFilters: "Afficher/Masquer les filtres",
          showHideSearch: "Afficher/Masquer la recherche",
          sortByColumnAsc: "Trier par {column} croissant",
          sortByColumnDesc: "Trier par {column} décroissant",
          sortedByColumnAsc: "Trié par {column} croissant",
          sortedByColumnDesc: "Trié par {column} décroissant",
          thenBy: ", puis par ",
          toggleDensity: "Basculer la densité",
          toggleFullScreen: "Basculer le plein écran",
          toggleSelectAll: "Basculer tout sélectionner",
          toggleSelectRow: "Basculer la sélection de ligne",
          toggleVisibility: "Basculer la visibilité",
          ungroupByColumn: "Dégrouper par {column}",
          unpin: "Détacher",
          unpinAll: "Tout détacher",
        }}
      />
    </Box>
  )
}
